import { useQuery } from "@tanstack/react-query";
import { widgetTypesService, type WidgetTypesListParams } from "@/shared/services/widgetTypes.service";

// ===== QUERY KEYS =====
export const WIDGET_TYPES_QUERY_KEYS = {
  all: ["widget-types"] as const,
  lists: () => [...WIDGET_TYPES_QUERY_KEYS.all, "list"] as const,
  list: (params?: WidgetTypesListParams) =>
    [...WIDGET_TYPES_QUERY_KEYS.lists(), params] as const,
  details: () => [...WIDGET_TYPES_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...WIDGET_TYPES_QUERY_KEYS.details(), id] as const,
};

// ===== QUERIES =====

/**
 * Hook to fetch widget types list
 * Uses caching since widget types rarely change
 */
export const useWidgetTypesQuery = (params?: WidgetTypesListParams) => {
  return useQuery({
    queryKey: WIDGET_TYPES_QUERY_KEYS.list(params),
    queryFn: () => widgetTypesService.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes cache - widget types don't change often
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook to fetch active widget types only
 * Optimized for the widget library component
 */
export const useActiveWidgetTypesQuery = () => {
  return useQuery({
    queryKey: WIDGET_TYPES_QUERY_KEYS.list({ active: true }),
    queryFn: () => widgetTypesService.list({ active: true }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch active widget types available for the current org's employee scope.
 * Used by the Studio widget palette so gated widgets (feature off for employees)
 * are not offered — avoids adding a block the beneficiary resolver would drop
 * (which leaves an empty grid cell). Kept separate from useActiveWidgetTypesQuery
 * so the app-wide WidgetTypesProvider keeps the full list for metadata lookups.
 */
export const useAvailableWidgetTypesQuery = () => {
  return useQuery({
    queryKey: WIDGET_TYPES_QUERY_KEYS.list({ active: true, availableOnly: true }),
    queryFn: () => widgetTypesService.list({ active: true, availableOnly: true }),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch a single widget type by ID
 */
export const useWidgetTypeQuery = (id: string) => {
  return useQuery({
    queryKey: WIDGET_TYPES_QUERY_KEYS.detail(id),
    queryFn: () => widgetTypesService.get(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
